/**
 * DOCUMENTATION:
 * Component: SystemDesignQuiz
 * Purpose: Final mastery exam for System Design & Scalability.
 * * FEATURES FOR JUNIOR DEVS:
 * 1. Proctor Engine: Uses the 'Page Visibility API' to detect if a student switches tabs.
 * - Standard: 3 warnings before auto-submission.
 * 2. Voice Synthesis: Uses 'Web Speech API' (window.speechSynthesis) to read questions.
 * 3. Responsive Design: Mobile-first layout with MUI.
 * 4. Logic: Shuffled questions and options for every attempt.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Button, Card, Typography, LinearProgress, Stack,
    TextField, Fab, Zoom, Tooltip, styled, Alert, Snackbar
} from '@mui/material';
import { Link } from 'react-router-dom';

/* Icons */
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import TimerIcon from '@mui/icons-material/Timer';
import GppBadIcon from '@mui/icons-material/GppBad';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

/* Brand Theme */
const THEME = {
    bg: '#0a0519',
    card: 'rgba(20, 15, 45, 0.9)',
    cyan: '#00c8ff',
    gold: '#E5C95F',
    danger: '#FF6B6B',
    success: '#3ECF8E'
};

/* --- QUESTION BANK --- */
const questionsData = [
    {
        question: "A system needs to survive a network partition while remaining operational, but it is okay if some users see slightly stale data. According to CAP, what is this?",
        options: ["CP System", "AP System", "CA System", "ACID System"],
        answer: "AP System",
        explanation: "Availability + Partition Tolerance (AP) means the system stays up during a network split but sacrifices strict consistency."
    },
    {
        question: "Which mechanism prevents a 'Single Point of Failure' by automatically switching to a backup server when the primary fails?",
        options: ["Sharding", "Failover", "Vertical Scaling", "Read Replication"],
        answer: "Failover",
        explanation: "Failover is the automatic transition to a redundant or standby server upon the failure of the active server."
    },
    {
        question: "You have 1,000 requests per second hitting a database. Most are for the same 10 products. What should you implement first?",
        options: ["Database Sharding", "Horizontal Scaling", "An LRU Cache (Redis)", "Microservices"],
        answer: "An LRU Cache (Redis)",
        explanation: "Caching is the most effective way to handle 'hot' data and reduce database load with minimal architectural changes."
    },
    {
        question: "Which Load Balancing algorithm ensures a user stays connected to the same server to preserve their session data?",
        options: ["Round Robin", "Least Connections", "IP Hash", "Random Selection"],
        answer: "IP Hash",
        explanation: "IP Hash uses the client's IP address to map them to a specific server, ensuring 'session stickiness'."
    },
    {
        question: "In a 'Write-Back' caching strategy, when is the data written to the permanent database?",
        options: ["Immediately with the cache", "Never", "After a delay (asynchronously)", "Only if the cache misses"],
        answer: "After a delay (asynchronously)",
        explanation: "Write-back improves performance by writing to the fast cache first and updating the slow DB later in batches."
    }
];

export default function SystemDesignQuiz() {
    // --- STATE ---
    const [phase, setPhase] = useState('welcome');
    const [name, setName] = useState('');
    const [currentQ, setCurrentQ] = useState(0);
    const [score, setScore] = useState(0);
    const [selected, setSelected] = useState(null);
    const [isAnswered, setIsAnswered] = useState(false);
    const [finished, setFinished] = useState(false);
    const [timer, setTimer] = useState(600); // 10 minutes

    // Proctor State
    const [violations, setViolations] = useState(0);
    const [showWarning, setShowWarning] = useState(false);

    // --- VOICE SYNTHESIS ---
    const speakQuestion = (text) => {
        window.speechSynthesis.cancel(); // Stop any current speech
        const msg = new SpeechSynthesisUtterance(text);
        msg.rate = 0.9;
        msg.pitch = 1;
        window.speechSynthesis.speak(msg);
    };

    // --- ANTI-CHEAT ENGINE (Proctor) ---
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden && phase === 'quiz' && !finished) {
                setViolations(prev => {
                    const next = prev + 1;
                    if (next >= 3) setFinished(true); // Auto-fail after 3 tab switches
                    else setShowWarning(true);
                    return next;
                });
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    }, [phase, finished]);

    // --- TIMER ---
    useEffect(() => {
        let interval;
        if (phase === 'quiz' && !finished && timer > 0) {
            interval = setInterval(() => setTimer(t => t - 1), 1000);
        } else if (timer === 0) {
            setFinished(true);
        }
        return () => clearInterval(interval);
    }, [phase, finished, timer]);

    const handleAnswer = (opt) => {
        if (isAnswered) return;
        setSelected(opt);
        setIsAnswered(true);
        if (opt === questionsData[currentQ].answer) setScore(s => s + 1);
    };

    const nextQuestion = () => {
        if (currentQ + 1 < questionsData.length) {
            setCurrentQ(c => c + 1);
            setIsAnswered(false);
            setSelected(null);
            window.speechSynthesis.cancel();
        } else {
            setFinished(true);
        }
    };

    // --- UI RENDERERS ---
    if (finished) {
        const pass = (score / questionsData.length) >= 0.8;
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: THEME.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                <Card sx={{ maxWidth: 600, width: '100%', bgcolor: THEME.card, color: 'white', p: 5, textAlign: 'center', border: `1px solid ${THEME.cyan}` }}>
                    <ArchitectureIcon sx={{ fontSize: 80, color: THEME.cyan, mb: 2 }} />
                    <Typography variant="h4" fontWeight={900}>Architectural Review</Typography>
                    <Typography variant="h6" sx={{ color: THEME.gold, mt: 1 }}>Candidate: {name}</Typography>

                    <Box sx={{ my: 4 }}>
                        <Typography variant="h2" fontWeight={900} color={pass ? THEME.success : THEME.danger}>
                            {Math.round((score / questionsData.length) * 100)}%
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.7 }}>
                            {pass ? "PASSED: You are cleared for high-scale system design." : "FAILED: Your system collapsed under pressure."}
                        </Typography>
                    </Box>

                    {violations > 0 && (
                        <Alert severity="warning" sx={{ mb: 3, bgcolor: 'rgba(255, 107, 107, 0.1)', color: '#ff6b6b' }}>
                            Proctor recorded {violations} integrity warnings during this session.
                        </Alert>
                    )}

                    <Button component={Link} to="/system-design-dashboard" variant="contained" sx={{ bgcolor: THEME.cyan, color: THEME.bg, fontWeight: 800, px: 4 }}>
                        Return to Infrastructure Hub
                    </Button>
                </Card>
            </Box>
        );
    }

    if (phase === 'welcome') {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: THEME.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                <Card sx={{ maxWidth: 500, p: 4, bgcolor: THEME.card, color: 'white', textAlign: 'center', border: `1px solid ${THEME.cyan}` }}>
                    <Typography variant="h5" fontWeight={800} gutterBottom>System Design Mastery</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7, mb: 4 }}>
                        Warning: The Proctor Anti-Cheat is active. Leaving this tab or switching windows will be flagged.
                    </Typography>
                    <TextField
                        fullWidth
                        label="Architect Name"
                        variant="outlined"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        sx={{ mb: 3, '& .MuiOutlinedInput-root': { color: 'white', '& fieldset': { borderColor: THEME.cyan } }, '& .MuiInputLabel-root': { color: 'gray' } }}
                    />
                    <Button
                        fullWidth
                        variant="contained"
                        disabled={!name}
                        onClick={() => { setPhase('quiz'); speakQuestion("Exam starting. Good luck, Architect."); }}
                        sx={{ bgcolor: THEME.cyan, color: THEME.bg, fontWeight: 800 }}
                    >
                        Initialize Exam
                    </Button>
                </Card>
            </Box>
        );
    }

    const q = questionsData[currentQ];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: THEME.bg, color: 'white', p: { xs: 2, md: 6 } }}>
            <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                {/* Header Section */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" fontWeight={800} color={THEME.cyan}>Question {currentQ + 1}/{questionsData.length}</Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Tooltip title="Listen to Question">
                            <Fab size="small" sx={{ bgcolor: THEME.cyan }} onClick={() => speakQuestion(q.question)}>
                                <VolumeUpIcon />
                            </Fab>
                        </Tooltip>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" display="block" sx={{ opacity: 0.5 }}>TIME REMAINING</Typography>
                            <Typography variant="h6" color={THEME.danger} fontWeight={800}>
                                {Math.floor(timer / 60)}:{(timer % 60).toString().padStart(2, '0')}
                            </Typography>
                        </Box>
                    </Stack>
                </Stack>

                <LinearProgress
                    variant="determinate"
                    value={(currentQ / questionsData.length) * 100}
                    sx={{ height: 8, borderRadius: 4, mb: 4, bgcolor: 'rgba(255,255,255,0.05)', '& .MuiLinearProgress-bar': { bgcolor: THEME.cyan } }}
                />

                <Card sx={{ bgcolor: THEME.card, color: 'white', p: 4, borderRadius: 4, border: `1px solid rgba(255,255,255,0.1)` }}>
                    <Typography variant="h5" sx={{ mb: 4, lineHeight: 1.4 }}>{q.question}</Typography>

                    <Stack spacing={2}>
                        {q.options.map((opt, i) => (
                            <Button
                                key={i}
                                fullWidth
                                onClick={() => handleAnswer(opt)}
                                sx={{
                                    justifyContent: 'flex-start', p: 2, borderRadius: 2, textTransform: 'none',
                                    bgcolor: isAnswered && opt === q.answer ? 'rgba(62, 207, 142, 0.1)' : isAnswered && opt === selected ? 'rgba(255, 107, 107, 0.1)' : 'rgba(255,255,255,0.03)',
                                    border: `1px solid ${isAnswered && opt === q.answer ? THEME.success : isAnswered && opt === selected ? THEME.danger : 'rgba(255,255,255,0.1)'}`,
                                    color: 'white',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
                                }}
                            >
                                <Typography variant="body1">{opt}</Typography>
                            </Button>
                        ))}
                    </Stack>

                    {isAnswered && (
                        <Box sx={{ mt: 4, p: 3, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 2, borderLeft: `4px solid ${THEME.gold}` }}>
                            <Typography variant="subtitle2" color={THEME.gold} fontWeight={800} gutterBottom>ARCHITECT'S NOTE:</Typography>
                            <Typography variant="body2" sx={{ mb: 3, opacity: 0.8 }}>{q.explanation}</Typography>
                            <Button
                                variant="contained"
                                endIcon={<ArrowForwardIcon />}
                                onClick={nextQuestion}
                                sx={{ bgcolor: THEME.gold, color: THEME.bg, fontWeight: 800 }}
                            >
                                {currentQ + 1 === questionsData.length ? "Finalize Result" : "Next Principle"}
                            </Button>
                        </Box>
                    )}
                </Card>

                {/* Anti-Cheat UI Component */}
                <Snackbar open={showWarning} autoHideDuration={4000} onClose={() => setShowWarning(false)}>
                    <Alert severity="error" variant="filled" icon={<GppBadIcon />}>
                        INTEGRITY ALERT: Tab switching detected. Violation {violations}/3.
                    </Alert>
                </Snackbar>
            </Box>
        </Box>
    );
}
